import { useRef } from 'react';

import { isFiniteNumber, type DisplayUnit } from '@core';
import type { CorridorParameterMargins } from '@data';

import { useResizeObserver } from '../../../../hooks/useResizeObserver';
import { Box } from '../../../Layout';
import { getOffsetPercent, type CorridorExtent } from '../../utils/corridors';

import { CorridorFrame } from './Components/CorridorFrame';
import { TwoCorridorLabels } from './Components/TwoCorridorLabels';
import { ValueMarker } from './Components/ValueMarker/ValueMarker';
import * as css from './ParameterCardCorridors.css';
import { ParameterCardSingleCorridor } from './ParameterCardSingleCorridor';

export type Corridor = Pick<CorridorParameterMargins, 'innerMin' | 'innerMax'>;

export type ParameterCardCorridorProps = {
    corridorParameterMargins?: Corridor | null;
    goalCorridor?: Corridor | null;
    unit: DisplayUnit;
    value?: number | null;
    indicatorColor?: 'green' | 'yellow' | 'red' | 'white';
};

const PERCENT_EXTENT: CorridorExtent = { min: 0, max: 100 };

export const ParameterCardCorridors = ({
    corridorParameterMargins,
    goalCorridor,
    unit,
    value,
    indicatorColor,
}: ParameterCardCorridorProps) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const { width = 0 } = useResizeObserver({ ref: trackRef });

    const areCorridorsEqual =
        corridorParameterMargins?.innerMax === goalCorridor?.innerMax &&
        corridorParameterMargins?.innerMin === goalCorridor?.innerMin;

    if (!corridorParameterMargins && !goalCorridor) return null;

    if (!corridorParameterMargins || !goalCorridor || areCorridorsEqual) {
        return (
            <ParameterCardSingleCorridor
                corridorParameterMargins={corridorParameterMargins}
                goalCorridor={goalCorridor}
                unit={unit}
                value={value}
                indicatorColor={indicatorColor}
            />
        );
    }

    const extent = {
        min: Math.min(corridorParameterMargins.innerMin, goalCorridor.innerMin),
        max: Math.max(corridorParameterMargins.innerMax, goalCorridor.innerMax),
    };

    const systemStart = getOffsetPercent(corridorParameterMargins.innerMin, extent);
    const systemEnd = getOffsetPercent(corridorParameterMargins.innerMax, extent);
    const goalStart = getOffsetPercent(goalCorridor.innerMin, extent);
    const goalEnd = getOffsetPercent(goalCorridor.innerMax, extent);

    const valuePosition = isFiniteNumber(value) ? getOffsetPercent(value, extent) : null;

    return (
        <Box position="relative" className={css.root}>
            <Box height="100%" position="relative" ref={trackRef}>
                <CorridorFrame tone="system" left={systemStart} right={systemEnd} />
                <CorridorFrame tone="goal" left={goalStart} right={goalEnd} />
                <TwoCorridorLabels
                    systemCorridor={corridorParameterMargins}
                    goalCorridor={goalCorridor}
                    extent={extent}
                    unit={unit}
                    trackWidth={width}
                />
                {valuePosition == null ? null : (
                    <ValueMarker value={valuePosition} extent={PERCENT_EXTENT} indicatorColor={indicatorColor} />
                )}
            </Box>
        </Box>
    );
};
